import React, { useState } from 'react';
import { Gift, Brain, Loader2, ArrowRight, MessageCircle, FileText } from 'lucide-react';
import { QuestionCard } from './QuestionCard';
import { GVVRVoiceOrchestrationDemo } from './voice/GVVRVoiceOrchestrationDemo';
import { RecipientChoice } from './RecipientChoice';
import { OccasionChoice } from './OccasionChoice';
import { FinalRecommendationsComponent } from './FinalRecommendations';
import { VersionBadge } from './VersionBadge';
import { 
  giverQuestions, 
  recipientQuickQuestions, 
  recipientFullQuestions, 
  occasionQuickQuestions, 
  occasionStandardQuestions 
} from '../data/questions';
import { 
  analyzeGiftGivingArchetype, 
  analyzeRecipientArchetype, 
  generateFinalRecommendations 
} from '../services/openai';
import { getShortVersion } from '../utils/version';
import type { 
  Answers, 
  ArchetypeAnalysis,
  RecipientAnalysis,
  FinalRecommendations,
  OptionHint, 
  AppFlow, 
  RecipientFlow, 
  OccasionFlow
} from '../types';

export const GVVRComplete: React.FC = () => {
  console.log('🔄 GVVRComplete component loaded - Version 3.1');
  
  // Main flow state
  const [currentFlow, setCurrentFlow] = useState<AppFlow>('giver');
  
  // Giver flow state
  const [giverFlowChoice, setGiverFlowChoice] = useState<'none' | 'questions' | 'voice'>('none');
  const [currentGiverQuestion, setCurrentGiverQuestion] = useState(0);
  const [giverAnswers, setGiverAnswers] = useState<Answers>({});
  const [nameInput, setNameInput] = useState('');
  const [showGiverResults, setShowGiverResults] = useState(false);
  const [giverAnalysis, setGiverAnalysis] = useState<ArchetypeAnalysis | null>(null);
  const [isAnalyzingGiver, setIsAnalyzingGiver] = useState(false);
  
  // Recipient flow state
  const [recipientFlow, setRecipientFlow] = useState<RecipientFlow>('choice');
  const [currentRecipientQuestion, setCurrentRecipientQuestion] = useState(0);
  const [recipientAnswers, setRecipientAnswers] = useState<Answers>({});
  const [recipientNameInput, setRecipientNameInput] = useState('');
  const [showRecipientResults, setShowRecipientResults] = useState(false);
  const [recipientAnalysis, setRecipientAnalysis] = useState<RecipientAnalysis | null>(null);
  const [isAnalyzingRecipient, setIsAnalyzingRecipient] = useState(false);
  
  // Occasion flow state  
  const [occasionFlow, setOccasionFlow] = useState<OccasionFlow>('choice');
  const [currentOccasionQuestion, setCurrentOccasionQuestion] = useState(0);
  const [occasionAnswers, setOccasionAnswers] = useState<Answers>({});
  const [occasionNameInput, setOccasionNameInput] = useState('');
  
  // Final recommendations state
  const [finalRecommendations, setFinalRecommendations] = useState<FinalRecommendations | null>(null);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);

  // Get current questions based on flow
  const getCurrentQuestions = () => {
    switch (currentFlow) {
      case 'giver':
        return giverQuestions;
      case 'recipient':
        return recipientFlow === 'quick' ? recipientQuickQuestions : recipientFullQuestions;
      case 'occasion':
        return occasionFlow === 'quick' ? occasionQuickQuestions : occasionStandardQuestions;
      default:
        return giverQuestions;
    }
  };

  const getCurrentAnswers = () => {
    switch (currentFlow) {
      case 'giver':
        return giverAnswers;
      case 'recipient':
        return recipientAnswers;
      case 'occasion':
        return occasionAnswers;
      default:
        return giverAnswers;
    }
  };

  const getCurrentQuestion = () => {
    switch (currentFlow) {
      case 'giver':
        return currentGiverQuestion;
      case 'recipient':
        return currentRecipientQuestion;
      case 'occasion':
        return currentOccasionQuestion;
      default:
        return currentGiverQuestion;
    }
  };

  const setCurrentAnswers = (answers: Answers) => {
    switch (currentFlow) {
      case 'giver':
        setGiverAnswers(answers);
        break;
      case 'recipient':
        setRecipientAnswers(answers);
        break;
      case 'occasion':
        setOccasionAnswers(answers);
        break;
    }
  };

  const getCurrentNameInput = () => {
    switch (currentFlow) {
      case 'giver':
        return nameInput;
      case 'recipient':
        return recipientNameInput;
      case 'occasion':
        return occasionNameInput;
      default:
        return nameInput;
    }
  };

  const setCurrentNameInput = (input: string) => {
    switch (currentFlow) {
      case 'giver':
        setNameInput(input);
        break;
      case 'recipient':
        setRecipientNameInput(input);
        break;
      case 'occasion':
        setOccasionNameInput(input);
        break;
    }
  };

  // Question navigation state
  const questions = getCurrentQuestions();
  const currentQuestion = getCurrentQuestion();
  const currentQ = questions[currentQuestion];
  const canGoBack = currentQuestion > 0;

  // Progress calculation
  const progress = (currentQuestion / questions.length) * 100;



  // Question handlers
  const handleAnswer = (answer: string | OptionHint | number | object) => {
    const questions = getCurrentQuestions();
    const answers = getCurrentAnswers();
    const currentQ = questions[currentQuestion];

    if ((currentQ.type === 'values' || currentQ.type === 'focus' || currentQ.type === 'emotion' || currentQ.type === 'attachment_cue' || currentQ.type === 'structured_interests') && currentQ.multiple) {
      // Handle multi-select
      const currentSelections = Array.isArray(answers[currentQ.id]) ? answers[currentQ.id] as (string | OptionHint)[] : [];
      
      const isAlreadySelected = currentSelections.some(item => 
        typeof item === 'object' && typeof answer === 'object' && 'text' in item && 'text' in answer
          ? item.text === answer.text
          : item === answer
      );

      if (isAlreadySelected) {
        const newSelections = currentSelections.filter(item =>
          typeof item === 'object' && typeof answer === 'object' && 'text' in item && 'text' in answer
            ? item.text !== answer.text
            : item !== answer
        );
        setCurrentAnswers({ ...answers, [currentQ.id]: newSelections });
      } else {
        if (currentSelections.length < (currentQ.maxSelections || 3)) {
          setCurrentAnswers({ ...answers, [currentQ.id]: [...currentSelections, answer] });
        }
      }
    } else {
      // Handle single-select
      setCurrentAnswers({ ...answers, [currentQ.id]: answer });
      handleNextQuestion();
    }
  };

  const handleNameSubmit = () => {
    const currentNameInput = getCurrentNameInput();
    if (currentNameInput.trim()) {
      const answers = getCurrentAnswers();
      setCurrentAnswers({ ...answers, [currentQ.id]: currentNameInput.trim() });
      handleNextQuestion();
    }
  };

  const handleContinueValues = (additionalText?: string) => {
    const answers = getCurrentAnswers();
    
    // If there's additional text for structured_interests, store it
    if (additionalText && currentQ.type === 'structured_interests') {
      setCurrentAnswers({ 
        ...answers, 
        [`${currentQ.id}_additional`]: additionalText 
      });
    }
    
    handleNextQuestion();
  };

  const handleNextQuestion = () => {
    const questions = getCurrentQuestions();
    
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      switch (currentFlow) {
        case 'giver':
          setCurrentGiverQuestion(prev => prev + 1);
          break;
        case 'recipient':
          setCurrentRecipientQuestion(prev => prev + 1);
          break;
        case 'occasion':
          setCurrentOccasionQuestion(prev => prev + 1);
          break;
      }
      // Only clear name input if we're currently in a name input question
      if (currentQ.type === 'name') {
        setCurrentNameInput('');
      }
    } else {
      // End of questions for current flow
      switch (currentFlow) {
        case 'giver':
          handleGiverComplete();
          break;
        case 'recipient':
          handleRecipientComplete();
          break;
        case 'occasion':
          handleOccasionComplete();
          break;
      }
    }
  };

  const handleGoBack = () => {
    if (currentQuestion > 0) {
      switch (currentFlow) {
        case 'giver':
          setCurrentGiverQuestion(prev => prev - 1);
          break;
        case 'recipient':
          setCurrentRecipientQuestion(prev => prev - 1);
          break;
        case 'occasion':
          setCurrentOccasionQuestion(prev => prev - 1);
          break;
      }
      // Only clear name input when going back to a name question
      const questions = getCurrentQuestions();
      const prevQuestion = questions[currentQuestion - 1];
      if (prevQuestion && prevQuestion.type === 'name') {
        setCurrentNameInput('');
      }
    }
  };

  // Flow completion handlers
  const handleGiverComplete = async () => {
    setIsAnalyzingGiver(true);
    try {
      const culturalContext = giverAnswers['2'] ? String(giverAnswers['2']) : undefined;
      
      const analysis = await analyzeGiftGivingArchetype({
        answers: giverAnswers,
        culturalContext
      });
      
      setGiverAnalysis(analysis);
      setShowGiverResults(true);
      setCurrentFlow('recipient');
      setCurrentGiverQuestion(0);
      setNameInput('');
    } catch (error) {
      console.error('Giver analysis failed:', error);
    } finally {
      setIsAnalyzingGiver(false);
    }
  };

  const handleRecipientComplete = async () => {
    setIsAnalyzingRecipient(true);
    try {
      const analysis = await analyzeRecipientArchetype({
        answers: recipientAnswers,
        profileType: recipientFlow === 'quick' ? 'quick' : 'full'
      });
      
      setRecipientAnalysis(analysis);
      setShowRecipientResults(true);
      setCurrentFlow('occasion');
      setCurrentRecipientQuestion(0);
      setNameInput('');
    } catch (error) {
      console.error('Recipient analysis failed:', error);
    } finally {
      setIsAnalyzingRecipient(false);
    }
  };

  const handleOccasionComplete = async () => {
    if (!giverAnalysis || !recipientAnalysis) return;
    
    setIsGeneratingRecommendations(true);
    try {
      // Parse occasion answers into context
      const occasionKey = occasionFlow === 'quick' ? 301 : 401;
      const emotionKey = occasionFlow === 'quick' ? 303 : 403;
      
      const context = {
        type: String(occasionAnswers[occasionKey] || 'Birthday').replace(/^\w+\s/, ''),
        budget: String(occasionAnswers[occasionFlow === 'quick' ? 302 : 402] || '$50-100'),
        timeline: 'Soon',
        emotionGoals: Array.isArray(occasionAnswers[emotionKey]) 
          ? (occasionAnswers[emotionKey] as (string | OptionHint)[]).map(goal => 
              typeof goal === 'object' ? goal.text : goal
            )
          : [typeof occasionAnswers[emotionKey] === 'object' && occasionAnswers[emotionKey] !== null && 'text' in occasionAnswers[emotionKey] 
              ? (occasionAnswers[emotionKey] as OptionHint).text 
              : String(occasionAnswers[emotionKey] || 'Show care')],
        specialContext: occasionAnswers[404] ? String(occasionAnswers[404]) : undefined,
        safetyVsSurprise: typeof occasionAnswers[405] === 'number' ? occasionAnswers[405] as number : 50
      };
      
      // Generate final recommendations
      const recommendations = await generateFinalRecommendations({
        giverProfile: {
          name: String(giverAnswers[1] || 'User'),
          archetype: giverAnalysis.primaryArchetype,
          confidence: giverAnalysis.confidence,
          traits: [],
          reasoning: giverAnalysis.reasoning,
          insights: giverAnalysis.personalizedInsights
        },
        recipientProfile: {
          name: recipientNameInput || 'Recipient',
          relationship: {
            type: String(recipientAnswers[recipientFlow === 'quick' ? 101 : 201] || 'Friend'),
            duration: String(recipientAnswers[recipientFlow === 'quick' ? 102 : 202] || 'Unknown')
          },
          archetype: recipientAnalysis.primaryArchetype,
          confidence: recipientAnalysis.confidence,
          traits: [],
          reasoning: recipientAnalysis.reasoning,
          insights: recipientAnalysis.personalizedInsights
        },
        occasionContext: context
      });
      
      setFinalRecommendations(recommendations);
      setCurrentFlow('recommendations');
    } catch (error) {
      console.error('Final recommendations failed:', error);
    } finally {
      setIsGeneratingRecommendations(false);
    }
  };

  // Voice flow handlers
  const handleGiverFlowChoice = (choice: 'questions' | 'voice') => {
    setGiverFlowChoice(choice);
    if (choice === 'questions') {
      setCurrentGiverQuestion(1); // Skip name question since we already have it
    }
  };



  const handleBackToGiverChoice = () => {
    setGiverFlowChoice('none');
    setCurrentGiverQuestion(0);
  };

  // Flow choice handlers
  const handleRecipientFlowChoice = (flow: RecipientFlow) => {
    setRecipientFlow(flow);
    setCurrentRecipientQuestion(0);
    // Don't clear recipientNameInput - we want to preserve the name!
    setShowGiverResults(false); // Hide giver results to show recipient questions
  };

  const handleOccasionFlowChoice = (flow: OccasionFlow) => {
    setOccasionFlow(flow);
    setCurrentOccasionQuestion(0);
    setOccasionNameInput('');
    setShowRecipientResults(false); // Hide recipient results to show occasion questions
  };

  const restartDemo = () => {
    setCurrentFlow('giver');
    setGiverFlowChoice('none');
    setCurrentGiverQuestion(0);
    setCurrentRecipientQuestion(0);
    setCurrentOccasionQuestion(0);
    setNameInput('');
    setRecipientNameInput('');
    setOccasionNameInput('');
    setIsAnalyzingGiver(false);
    setIsAnalyzingRecipient(false);
    setIsGeneratingRecommendations(false);
    setGiverAnswers({});
    setRecipientAnswers({});
    setOccasionAnswers({});
    setGiverAnalysis(null);
    setRecipientAnalysis(null);
    setFinalRecommendations(null);
    setShowGiverResults(false);
    setShowRecipientResults(false);
  };

  // Show voice conversation if selected for giver flow
  if (currentFlow === 'giver' && giverFlowChoice === 'voice') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        {/* Back button */}
        <div className="p-6">
          <button
            onClick={handleBackToGiverChoice}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            ← Back to Options
          </button>
        </div>
        <GVVRVoiceOrchestrationDemo />
      </div>
    );
  }

  // Show giver flow choice after name is entered
  if (currentFlow === 'giver' && giverFlowChoice === 'none' && giverAnswers['1']) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Gift className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-bold text-gray-800">GVVR</span>
              <VersionBadge />
            </div>
            <p className="text-gray-600">Your complete gift curation system</p>
          </div>

          {/* Flow Choice */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Hi {String(giverAnswers['1'] || nameInput.trim())}! How would you like to continue?
            </h2>
            <p className="text-gray-600 mb-8 text-center">
              Choose your preferred way to analyze your gift-giving personality
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Voice Conversation Option */}
              <button
                onClick={() => handleGiverFlowChoice('voice')}
                className="group p-6 border-2 border-gray-200 rounded-2xl hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200"
              >
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 text-indigo-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Voice Conversation
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Talk naturally with Grace, our AI gift expert. Just speak about your gift-giving style like you would with a friend.
                  </p>
                  <div className="text-xs text-indigo-600 font-medium">
                    ⏱️ 3-5 minutes • 🎯 Natural & conversational
                  </div>
                </div>
              </button>

              {/* Question Flow Option */}
              <button
                onClick={() => handleGiverFlowChoice('questions')}
                className="group p-6 border-2 border-gray-200 rounded-2xl hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-200"
              >
                <div className="text-center">
                  <FileText className="w-12 h-12 text-indigo-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Interactive Questions
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Answer carefully crafted questions that help us understand your unique gift-giving approach.
                  </p>
                  <div className="text-xs text-indigo-600 font-medium">
                    ⏱️ 2-3 minutes • 🎯 Quick & structured
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show giver archetype result as transition (priority over other flows)
  if (showGiverResults && giverAnalysis) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Transition Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Gift className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-bold text-gray-800">GVVR</span>
              <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs font-medium rounded-full">
                {getShortVersion()}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Great! We've analyzed your giving style
            </h1>
            <div className="flex items-center justify-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">✓</div>
                Part 1: Giver Profile
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
                Part 2: Recipient Profile
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2 text-gray-400">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold">3</div>
                Part 3: Gift Occasion
              </span>
            </div>
          </div>

          {/* Quick Giver Summary */}
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Your Gift-Giving Archetype: {giverAnalysis.primaryArchetype}
              </h2>
              <p className="text-gray-600 text-lg">{giverAnalysis.confidence}% confidence</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {giverAnalysis.personalizedInsights.map((insight: string, index: number) => (
                <div key={index} className="p-4 bg-indigo-50 rounded-xl">
                  <p className="text-indigo-700 text-sm">{insight}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recipient Choice */}
          <RecipientChoice
            recipientName={recipientNameInput}
            setRecipientName={setRecipientNameInput}
            onFlowChoice={handleRecipientFlowChoice}
          />
        </div>
      </div>
    );
  }

  // Show occasion choice
  if (showRecipientResults && recipientAnalysis) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Transition Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Gift className="w-8 h-8 text-indigo-600" />
              <span className="text-2xl font-bold text-gray-800">GVVR</span>
              <VersionBadge />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Excellent! Now we understand {recipientNameInput}'s personality
            </h1>
            <div className="flex items-center justify-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">✓</div>
                Part 1: Giver Profile
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">✓</div>
                Part 2: Recipient Profile
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
                Part 3: Gift Occasion
              </span>
            </div>
          </div>

          {/* Quick Recipient Summary */}
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {recipientNameInput}'s Archetype: {recipientAnalysis.primaryArchetype}
              </h2>
              <p className="text-gray-600 text-lg">{recipientAnalysis.confidence}% confidence</p>
            </div>
            {recipientAnalysis.personalizedInsights && recipientAnalysis.personalizedInsights.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                {recipientAnalysis.personalizedInsights.map((insight: string, index: number) => (
                  <div key={index} className="p-4 bg-purple-50 rounded-xl">
                    <p className="text-purple-700 text-sm">{insight}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Occasion Choice */}
          <OccasionChoice
            recipientName={recipientNameInput}
            onFlowChoice={handleOccasionFlowChoice}
          />
        </div>
      </div>
    );
  }

  // Show final recommendations
  if (currentFlow === 'recommendations' && finalRecommendations && giverAnalysis && recipientAnalysis) {
    return (
      <FinalRecommendationsComponent
        recommendations={finalRecommendations}
        giverProfile={{
          name: String(giverAnswers[1] || 'User'),
          archetype: giverAnalysis.primaryArchetype,
          confidence: giverAnalysis.confidence,
          traits: [],
          reasoning: giverAnalysis.reasoning,
          insights: giverAnalysis.personalizedInsights
        }}
        recipientProfile={{
          name: recipientNameInput || 'Recipient',
          relationship: {
            type: String(recipientAnswers[recipientFlow === 'quick' ? 101 : 201] || 'Friend'),
            duration: String(recipientAnswers[recipientFlow === 'quick' ? 102 : 202] || 'Unknown')
          },
          archetype: recipientAnalysis.primaryArchetype,
          confidence: recipientAnalysis.confidence,
          traits: [],
          reasoning: recipientAnalysis.reasoning,
          insights: recipientAnalysis.personalizedInsights
        }}
        occasionContext={{
          type: String(occasionAnswers[occasionFlow === 'quick' ? 301 : 401] || 'Birthday'),
          budget: String(occasionAnswers[occasionFlow === 'quick' ? 302 : 402] || '$50-100'),
          timeline: 'Soon',
          emotionGoals: ['Show care']
        }}
        onRestart={restartDemo}
      />
    );
  }

  // Show loading state for recipient analysis
  if (isAnalyzingRecipient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
            <div className="absolute inset-0 w-16 h-16 border-4 border-purple-200 rounded-full mx-auto"></div>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Analyzing {recipientNameInput}'s personality...
          </h2>
          
          <p className="text-gray-600 max-w-md mx-auto">
            Our AI is processing their responses to determine their gift-receiving archetype and preferences.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state for final recommendations
  if (isGeneratingRecommendations) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
            <div className="absolute inset-0 w-16 h-16 border-4 border-purple-200 rounded-full mx-auto"></div>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Generating personalized recommendations...
          </h2>
          
          <p className="text-gray-600 max-w-md mx-auto">
            Our AI is analyzing your profiles and creating the perfect gift suggestions based on psychology and your unique relationship dynamic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Gift className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-800">GVVR {getShortVersion()}</h1>
          </div>
          
          {currentFlow === 'giver' && (
            <p className="text-gray-600">
              Let's understand your gift-giving style through evidence-based psychology
            </p>
          )}
          {currentFlow === 'recipient' && (
            <p className="text-gray-600">
              Tell me about the person you're shopping for
            </p>
          )}
          {currentFlow === 'occasion' && (
            <p className="text-gray-600">
              Let's understand the occasion and context
            </p>
          )}
        </div>

        {/* Flow Navigation */}
        <div className="mb-8">
          <div className="flex justify-center space-x-4 mb-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              currentFlow === 'giver' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <Brain className="w-4 h-4" />
              Your Style
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              currentFlow === 'recipient' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <Gift className="w-4 h-4" />
              Recipient
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              currentFlow === 'occasion' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <MessageCircle className="w-4 h-4" />
              Occasion
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              currentFlow === 'recommendations' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <FileText className="w-4 h-4" />
              Results
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center mt-2">
            <span className="text-sm text-gray-500">
              Question {currentQuestion + 1} of {questions.length}
            </span>
          </div>
        </div>

        {/* Question Card */}
        <QuestionCard
          question={currentQ}
          nameInput={getCurrentNameInput()}
          setNameInput={setCurrentNameInput}
          answers={getCurrentAnswers()}
          onAnswer={handleAnswer}
          onNameSubmit={handleNameSubmit}
          onContinueValues={handleContinueValues}
          onGoBack={handleGoBack}
          canGoBack={canGoBack}
          isAnalyzing={
            (currentFlow === 'giver' && isAnalyzingGiver) ||
            (currentFlow === 'recipient' && isAnalyzingRecipient)
          }
        />

        {/* Demo Note */}
        <div className="text-center text-sm text-gray-500">
          <p>This is a demo of GVVR's complete psychological profiling system - Version {getShortVersion()}</p>
        </div>
      </div>
    </div>
  );
}; 